import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Zap, Shield, Crown, Sparkles, ArrowRight } from 'lucide-react';
import PayPalButton from './PayPalButton';
import { useAuth } from '../contexts/AuthContext';
import { purchasePlan } from '../services/firebase';
import { PLANS } from '../constants';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
  const { user, refreshCredits } = useAuth();
  const [successData, setSuccessData] = useState<{credits: number, tier: string} | null>(null);
  
  // Reset state whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setSuccessData(null);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;

  const handlePaymentSuccess = async (credits: number, tier: any) => {
    if (user) {
      // Execute Purchase in Firebase
      await purchasePlan(user.uid, credits, tier);
      await refreshCredits();
      // Show success view instead of ugly alert
      setSuccessData({ credits, tier });
    }
  };

  // --- RENDER: SUCCESS VIEW ---
  if (successData) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/90 backdrop-blur-md transition-opacity" onClick={onClose}></div>
        
        <div className="relative z-10 w-full max-w-md bg-gray-900/80 border border-green-500/30 rounded-3xl p-8 shadow-[0_0_50px_rgba(34,197,94,0.2)] backdrop-blur-xl animate-in zoom-in-95 duration-300 text-center flex flex-col items-center">
           {/* Ambient Glow */}
           <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
             <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-green-500/20 blur-[60px]"></div>
           </div>

           {/* Success Icon */}
           <div className="relative z-10 w-20 h-20 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mb-6 border border-green-500/30 shadow-[0_0_15px_rgba(34,197,94,0.3)]">
              <CheckCircle className="w-10 h-10 text-green-400" />
           </div>
           
           <h2 className="relative z-10 text-3xl font-bold text-white mb-2">Payment Successful!</h2>
           <p className="relative z-10 text-gray-400 mb-8 text-lg">
             You now have <span className="text-white font-bold">{successData.credits} credits</span> and access to the <span className="text-green-400 font-bold uppercase tracking-wider">{successData.tier}</span> plan features.
           </p>
           
           <button 
             onClick={onClose}
             className="relative z-10 w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-green-900/20 flex items-center justify-center gap-2 group"
           >
             Continue to Dashboard
             <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
           </button>
        </div>
      </div>
    );
  }

  // --- RENDER: PRICING OPTIONS ---
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose}></div>
      
      <div className="relative z-10 w-full max-w-5xl animate-in zoom-in-95 duration-300">
        <button onClick={onClose} className="absolute -top-12 right-0 text-gray-400 hover:text-white transition-colors p-2 bg-white/5 rounded-full hover:bg-white/10">
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-white mb-3 tracking-tight">Choose Your Plan</h2>
          <p className="text-gray-400 text-lg">Select the package that fits your needs. Pay once, use whenever.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const isRecommended = plan.recommended;
            const Icon = plan.id === 'pro' ? Crown : plan.id === 'creator' ? Zap : Shield;

            return (
              <div 
                key={plan.id}
                className={`
                  relative flex flex-col p-6 rounded-3xl border transition-all duration-300 backdrop-blur-sm
                  ${isRecommended 
                    ? 'bg-gradient-to-b from-blue-900/40 to-blue-950/40 border-blue-500/50 shadow-2xl shadow-blue-900/20 scale-105 z-10' 
                    : 'bg-gray-900/60 border-gray-700/50 hover:border-gray-600 hover:bg-gray-900/80'}
                `}
              >
                {isRecommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-xs font-bold tracking-wider shadow-lg flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> MOST POPULAR
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-xl ${isRecommended ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-800 text-gray-400'}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">{plan.tier} Tier</p>
                  </div>
                </div>

                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold text-white">${plan.price}</span>
                  <span className="text-gray-400 text-sm">/ {plan.credits} videos</span>
                </div>

                <div className="space-y-4 flex-1 mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm text-gray-300">
                      <CheckCircle className={`w-4 h-4 shrink-0 ${isRecommended ? 'text-blue-400' : 'text-gray-500'}`} />
                      {feature}
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  <PayPalButton 
                    amount={plan.price} 
                    onSuccess={() => handlePaymentSuccess(plan.credits, plan.tier)} 
                  />
                  <p className="text-[10px] text-center text-gray-500 mt-3">
                    ${(plan.price / plan.credits).toFixed(2)} per video
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingModal;